// frontend/src/components/layout/Navbar.jsx
// Polar Night top bar (DESIGN.md "Shell"): a thin glass strip. Breadcrumb on
// the left, the command palette trigger in the centre, the coach presence
// and the availability pill on the right. The account lives at the foot of the
// sidebar, so it is not repeated here.

import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import AvailabilityPill from "../shell/AvailabilityPill";
import { getBreadcrumbs } from "../shell/navigation";
import {
  titleFromCrumbs,
  usePageRecordFor,
  withRecord,
} from "../shell/pageTitle";
import CoachPresence from "../coach/CoachPresence";
import { pushRecent, recordKindFor } from "../shell/recentStore";
import { useAuth } from "../../contexts/AuthContext";
import { ChevronRightIcon, MenuIcon, SearchIcon } from "../shell/icons";

const isMac = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "");

const Navbar = ({
  toggleSidebar,
  isMobile = false,
  sidebarOpen = false,
  scrolled = false,
  onOpenPalette,
}) => {
  const location = useLocation();
  const record = usePageRecordFor(location.pathname);
  const crumbs = withRecord(getBreadcrumbs(location.pathname), record, location.pathname);
  const title = titleFromCrumbs(crumbs);
  // The tab names the page (WCAG 2.4.2). A page that sets its own title
  // (Dialer) runs its effect after this one on mount.
  useEffect(() => {
    document.title = title;
  }, [title]);
  // A named record (a contact, deal, company…) joins the palette's Recent.
  const userId = useAuth()?.user?.id;
  const recordLabel = typeof record === "string" ? record : null;
  useEffect(() => {
    const kind = recordKindFor(location.pathname);
    if (!recordLabel || !kind) return;
    pushRecent(userId, { path: location.pathname, label: recordLabel, kind });
  }, [recordLabel, location.pathname, userId]);
  const mac = isMac();
  // A one-level page names itself in its own H1, so the trail shows only
  // for nested pages (Organization › People). A single crumb comes in as a
  // compact title once that H1 has scrolled under the bar.
  const nested = crumbs.length > 1;

  return (
    <header className="sg-topbar" data-scrolled={scrolled || undefined}>
      <div className="sg-top-left">
        <button
          type="button"
          onClick={toggleSidebar}
          className="sg-icon-btn sg-menu-toggle"
          data-mobile={isMobile || undefined}
          aria-label="Toggle sidebar"
          data-hint="Menu"
          aria-expanded={sidebarOpen}
          aria-controls="workspace-navigation"
        >
          <MenuIcon />
        </button>

        <nav
          aria-label="Breadcrumb"
          className="sg-crumbs"
          data-single={nested ? undefined : true}
        >
          <ol>
            {crumbs.map((crumb, index) => {
              const last = index === crumbs.length - 1;
              return (
                <li key={`${crumb.path}-${crumb.label}`}>
                  {last ? (
                    <span aria-current="page" className="sg-crumb-current">
                      {crumb.label}
                    </span>
                  ) : (
                    <>
                      <Link to={crumb.path} className="sg-crumb-link">
                        {crumb.label}
                      </Link>
                      <ChevronRightIcon size={12} className="sg-crumb-sep" />
                    </>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>

      {onOpenPalette && (
        <div className="sg-top-center">
          <button
            type="button"
            onClick={onOpenPalette}
            className="sg-palette-trigger"
            aria-label="Open command palette"
            aria-keyshortcuts="Meta+K Control+K"
          >
            <SearchIcon size={14} />
            <span className="sg-palette-trigger-text">Search, jump or dial</span>
            <span className="sg-palette-trigger-keys" aria-hidden="true">
              <kbd className="sg-kbd">{mac ? "⌘" : "Ctrl"}</kbd>
              <kbd className="sg-kbd">K</kbd>
            </span>
          </button>
        </div>
      )}

      <div className="sg-top-right">
        {onOpenPalette && (
          <button
            type="button"
            onClick={onOpenPalette}
            className="sg-icon-btn sg-palette-icon"
            aria-label="Search"
            data-hint="Search"
            data-hint-keys={mac ? "⌘ K" : "Ctrl K"}
          >
            <SearchIcon />
          </button>
        )}
        <CoachPresence mac={mac} />
        <AvailabilityPill compact={isMobile} />
      </div>
    </header>
  );
};

export default Navbar;
